import { MouseEvent } from 'react';
import { Socket } from 'socket.io-client';
import { Map } from 'yjs';
import { Attachment } from './editor-attachment';
import { ThreadEditor } from './editor-comments';
import { Cta } from './editor-cta';
import { AppLimitError } from './editor-error';
import { MentionClickFn } from './editor-mention';
import { NoteDto } from './note';
import { TasksListType } from './tasks-calendar';
import { TranslatesFnType } from './translations';
import { User } from './user';

export interface EditorRange {
  start: {
    offset: number;
  };
  end: {
    offset: number;
  };
  native: Range;
}

export interface EditorScroll {
  length(): number;
  domNode?: HTMLElement;
  selection: {
    getRange: () => EditorRange[];
    setRange: ({ index, length }: { index: number; length: number }) => void;
  };
  selectedBlocks: Blot[];
  getSelectedBlocks: () => Blot[];
  getSelection: () => { index: number; length: number };
  setSelection: (index: number, length: number) => void;
  addBlot: (
    blotName: string,
    options?: Record<string, unknown>
  ) => Record<string, unknown> & {
    id: string;
  };
  children: {
    map: (blot: Blot) => Blot[];
    head: Blot;
    tail: Blot;
  };
  descendants: (fn: (blot: Blot) => boolean) => Blot[];
  getBlotById(id: string): Blot;
}

export interface ScrollServer {
  scrollToBlot: (id: string, isSmooth: boolean, isForce: boolean) => void;
}

export interface NotificationManager {
  toast: (message: string) => void;
}

export type EditorTheme = 'dark' | 'light';

export interface EditorPlugins {
  scrollSaver?: ScrollServer;
  commentManager?: CommentManager;
  notificationManager?: NotificationManager;
  editorStyle: {
    setTheme: (theme: EditorTheme) => void;
    getStyle: () => { size: string; style: string };
  };
  boardManager?: {
    convertColumnToTaskList: (boardId: string, taskListId: string, beforeColumnId: string) => void;
    destroy: () => void;
  };
  tasksManager?: {
    taskListRemoved: (taskListId: string) => void;
    convertToBoard: (boardId: string, taskListId: string) => void;
    destroy: () => void;
  };
  ctaManager: {
    setCta: (cta: Cta[]) => Promise<void>;
  };
  search?: {
    options: {
      container: HTMLDivElement | null;
      onSearchStart?: () => void;
      onSearchEnd?: () => void;
    };
    start: () => void;
  };
  aiManager?: {
    promptForPage: () => void;
  };
  linkManager?: {
    processNavigation: (ur: string) => boolean;
  };
  attachmentManager?: {
    options: {
      attachments: Attachment[];
      staticAssetPrefix: string;
      workspaceId: string;
      getAttachmentLink: (globalId: string, displayName: string) => string;
    };
    findBlotsWithAttachment: (attachGlobalId: string) => {
      blot: Blot;
    }[];
  };
  imageManager?: {
    getAllImageAttachmnets: () => Attachment[];
  };
  pasteManager: {
    paste: (options: { nimbusOps: unknown; html: string; text: string }) => void;
    pasteNimbusOps: (tempalePage: Record<string, unknown>) => void;
  };
  changeButton: {
    setOption: (key: string, value: unknown) => void;
    setOptions: (options: Record<string, unknown>) => void;
  };
}

export interface ProviderConfig {
  docName: string;
  connectToSocket?: boolean;
  connectToSocketWithDump?: boolean;
  disconnectWhenSynced?: boolean;
}

export interface onAttachmentFullScreenChangeParams {
  attachment: {
    globalId: string;
    displayName: string;
    mime: string;
  };
  open: boolean;
  blotId: string;
}

type EditorEventMap = {
  'attachment-fullscreen-change': onAttachmentFullScreenChangeParams;
};

type EditorEventCallback<E extends keyof EditorEventMap> = (params: EditorEventMap[E]) => void;

export interface HTMLElementWithBlot extends HTMLElement {
  __blot: {
    blot: Blot;
  };
}

interface EditorEmitter {
  emit: (event: string, options: Record<string, unknown>) => void;
  on: <T = Record<string, unknown>>(event: string, cb: (params: T) => void) => void;
  once: (event: string, cb: (params: Record<string, unknown>) => void) => void;
  off: (event: string, cb: (params: Record<string, unknown>) => void) => void;
}

export type EditorInstance = Record<string, unknown> & {
  editorId: number;
  blockId?: string;
  scroll: EditorScroll;
  plugins: EditorPlugins;
  mode: 'readonly' | 'edit';
  options: {
    scale?: number;
    theme: EditorTheme;
    providerConfig: ProviderConfig;
    serverRoom?: string;
    disallowInterfacesOutOfEditor?: boolean;
    disableOpenComments?: boolean;
  };
  destroy: () => void;
  destroyed: boolean;
  findById: (blotId: string) => Blot | null;
  parchment: {
    query: (blotName: string) => unknown;
  };
  store: {
    y: {
      wspoolHasChangedWithoutSave: () => boolean;
      isChangedWithoutSave: () => boolean;
      readyToDestroy: boolean;
    };
    rootChildren: {
      toJSON: () => string[];
    };
    createMapForBlot: (blotClass: unknown, options: Record<string, unknown>) => Map<Record<string, unknown>>;
  };
  exec: (event: string, params?: Record<string, unknown>) => void;
  emitter: EditorEmitter;
  getScrollingContainer: () => HTMLElement;
  focus?: () => void;
  findBlotsWithAttachment: () => {
    attachmentGlobalId: string;
    blot: Blot;
    blotName: string;
  }[];
  container: HTMLElement;
  applyContents: (
    ops: unknown[],
    options?: {
      start?: boolean;
      end?: boolean;
      before?: string;
      after?: string;
    }
  ) => void;
  getContents: () => unknown[];
  on<E extends keyof EditorEventMap>(event: E, cb: EditorEventCallback<E>): void;
  on(event: string, cb: (params: unknown) => void): void;
  off: (event: string, cb: (params: Record<string, unknown>) => void) => void;
  setFocus: (focus?: boolean) => void;
};

export interface Blot {
  id: string;
  getCommentsAnchorNode: () => HTMLElement;
  domNode: HTMLElement;
  getText?: () => string | undefined;
  constructor: {
    blotName: string;
  };
  getCellLink?: (position: TableCellPosition) => {
    getNode: () => HTMLElement;
  };
  remove: () => void;
  render: () => void;
  children: {
    head: Blot | null;
    tail: Blot | null;
  };
  props: Record<string, unknown>;
  scroll: {
    getSelection: () => { index: number; length: number };
    setSelection: (index: number, length: number) => void;
  };
  focusableFocus: () => void;
  embeddableFocus: () => void;
  getBlotName: () => string;
  isText?: boolean;
  next?: Blot;
  insertAt: (index: number, value?: unknown, def?: unknown) => void;
  addCaption?: () => void;
}

export interface ImageWrapperBlot extends Blot {
  image: FileBlot;
}

export interface EditorFile {
  instance: File | Blob;
}

export interface FileBlot extends Blot {
  setHTMLFileForUpload: (file: EditorFile) => void;
}

interface ColorMode {
  theme: string;
  bgColor: string;
}

/**
 * Shows which users have access to the page:
 *
 * - if null or undefined - all users have
 * - if {} - no users have
 * - otherwise - only users with keys have access
 */
export type EditorPageUsersAccess = Record<number, boolean>;

export type TranscriptActionType = 'guide' | 'transcript' | 'summary' | 'meeting_notes' | 'ask_ai';
export interface EditorCreateNoteParams {
  workspaceId: string;
  title: string;
  parentId: string;
  globalId: string;
}

export interface EditorOptions {
  workspaceId?: string;
  note: NoteDto;
  gatewayUrl: string;
  sessionId: string | null;
  orgId?: string;
  user: User;
  userRole: string;
  workspaceUserRole?: string;
  isWorkspaceMember: boolean;
  shared_url?: string;
  staticAssetPrefix: string;
  portalSettingsWorkspace?: string;
  language: string;
  HTMLPublicKey: string;
  dumpServerUrl: string;
  tasksBundleJsUrl: string;
  tasksBundleCssUrl: string;
  tasksAllowedActions?: object;
  commentsBundleJsUrl: string;
  aiServiceUrl: string;
  markdownEnabled: boolean;
  showLinkPanel: boolean;
  lightMode: ColorMode;
  darkMode: ColorMode;
  mode?: 'readonly' | 'edit';
  dump?: string | null;
  connectToSocket?: boolean;
  socket?: Socket | null;
  dateTimeLocale?: string;
  showToast?: (params: { title?: string; description?: string }) => void;
  onLoad: (editor: EditorInstance) => void;
  onMentionClick?: MentionClickFn;
  scrollingContainer?: string | HTMLElement;
  boundariesElement?: string | HTMLElement;
  showCopyBlockButton: boolean;
  isExtendedManageEnabled?: boolean;
  canBlotLocking?: boolean;
  socketServerUrl?: string;
  onSetHintIcon?: (blotId: string, target: HTMLElement) => Promise<Record<string, unknown>>;
  iframelyHost?: string;
  onGetTextFromImage?: (attachmentId: string) => void;
  drawPopupMenuInPortal?: boolean;
  scrollSaverScrollingContainer?: string;
  portalId?: string;
  usersAccess?: EditorPageUsersAccess | null;
  embeddedMenuMoreAnchorOptions?: {
    placement?: string;
    behavior?: Array<'bottom' | 'top'>;
    offset?: string;
    priority?: Array<'left' | 'right' | 'top' | 'bottom'>;
    boundariesElement?: 'viewport' | HTMLElement;
    overflowBoundariesElement?: 'viewport' | HTMLElement;
    positionFixed?: boolean;
  };
  canAddMembers?: boolean;
  dontHandleSyntaxHeaderClick?: boolean;
  disallowInterfacesOutOfEditor?: boolean;
  /* limit handlers */
  showAiLimitPopup?: (params: { max: number; current: number }) => void;
  showLimitPopup?: (limitError: AppLimitError) => void;
  showUsageLimitPopup?: (limitError: AppLimitError) => void;
  /**
   * max attachment size in bytes to upload from editor, if not specified no limit is applied
   */
  maxAttachmentSize?: number;
  /**
   * org usage
   */
  getStorageUsage?: () => Promise<{
    maxStorage: number;
    currentStorage: number;
  }>;
  incStorageSize?: (size: number) => void;
  /**
   * Decrease current storage size
   */
  decStorageSize?: (size: number) => void;
  /**
   * blots limit options
   */
  blotsLimit?: {
    enabled?: boolean;
    /**
     *  number of blots that can be added to the editor
     */
    limit?: number;
    /**
     * this function is called when the blots limit is reached
     */
    alert?: () => void;
  };
  /**
   * function to create a new note
   */
  createNote?: (params: EditorCreateNoteParams) => Promise<NoteDto | undefined>;
  onTaskListRender?: (node: Element, taskListId: string, type: TasksListType, options: Record<string, unknown>) => void;
  onKanbanRender?: (node: Element, taskListId: string, type: TasksListType, options: Record<string, unknown>) => void;
  onTasksFeatureComponentPropsChanged?: (featureId: string, props: Record<string, unknown>) => void;
  showImageViewerForPage?: (payload: { attachmentGlobalId: string; editorInstance: EditorInstance | null }) => void;
  enableImageViewer?: boolean;
  saveUserTasksVars?: (
    taskListId: string,
    { calendarView, blockView }: { calendarView?: string; blockView?: string }
  ) => void;
  getUserTasksVars?: (taskListId: string) => Record<string, string> | null;
  emptyFeaturesPlugin?: {
    enabled: boolean;
    useTemplate?: () => void;
    invitePeople?: () => void;
    import?: () => void;
  };
  isTranscriptEnabled?: boolean;
  /**
   * Whether to enable the guide generation button on videos
   */
  isGuidegenEnabled?: boolean;
  /**
   * One of the transcript buttons on a video is clicked
   * @param params
   * @param action
   * @returns
   */
  onTranscript?: (
    params: {
      url: string;
      globalId: string;
      displayName: string;
      duration: number;
      workspaceId: string;
      videoElement: HTMLVideoElement;
    },
    action: TranscriptActionType
  ) => void;
  /**
   * whether the rate editor menu is enabled
   */
  rateEditorEnabled?: boolean;
  /**
   * function that is called when the rate editor menu is clicked
   */
  onRateEditorClick?: () => void;
  applyTemplate?: (editor: EditorInstance) => void;
  changeButton?: {
    enabled: boolean;
    onEditClick?: () => boolean;
    panelClassNames?: string;
    undoClassNames?: string;
    redoClassNames?: string;
    editClassNames?: string;
    eyeClassNames?: string;
    showEyeIcon?: boolean;
    eyeIconClosed?: boolean;
  };
  disableComments?: boolean;
  disableOpenComments?: boolean;
  disableAddNote?: boolean;
  enableAiManager?: boolean;
  disableConvertBlotManagerAnchorOptions?: boolean;

  /**
   * Called when new attachment is created in editor
   */
  onAttachmentCreated?: (attachment: { globalId: string; mime: string }) => void;
  onAttachmentFullScreenChange?: (params: onAttachmentFullScreenChangeParams) => void;

  /**
   * Called when click on editor
   */
  onClick?: (event: MouseEvent, editor: EditorInstance | null) => void;
}

export interface EditorReactProps {
  /**
   * activates the edit mode for touch devices,
   * only works when options.mode is 'edit'
   */
  touchDeviceEditMode?: boolean;
  applyMobileStyles?: boolean;
  options: EditorOptions;
  t?: TranslatesFnType;
}

export interface EditorReactState {
  bgColor: string;
}

interface CellRef {
  getNode: () => HTMLElement;
  getText?: () => string | undefined;
}

export interface CommentManager {
  setThreads: (threadsInfo: ThreadEditor[] | undefined) => void;
  getTableCellNodeByThreadId: (threadId: string) => CellRef;
  comments: ThreadEditor[];
  getThreadPreview: (threadId: string) => Record<string, string> | undefined;
  options: {
    initPluginOnEditorAvailable: () => void;
    openCommentsByThreadId: (initOpenThreadId: string, commentId: string) => void;
    setOnCommentsLoaded: (cb: () => void) => void;
    setThreadIdToOpen: (threadId: string) => void;
    getPluginInstance: () => CommentManager;
    disableOpenComments?: boolean;
  };
  onEditorUpdate: () => void;
  onEditorProcessResize: () => void;
  onEditorProcessResizeEnd: () => void;
  destroy: () => void;
  disableOpenComments?: boolean;
}

export interface TableCellPosition {
  row: number;
  col: number;
}

export enum BlotsLockState {
  FULL = 'full',
  CAN_UNLOCK = 'canUnlock',
  DISABLED = 'disabled',
}

declare global {
  interface Window {
    gEditor: EditorInstance | undefined;
    __editorThemeChangeInit: boolean | undefined;
  }
}
